import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, RefreshCcw, Eye, Calendar, Users } from 'lucide-react';
type Tab = 'monthly' | 'private' | 'single';
interface Plan {
  name: string;
  freq: string;
  ageNote?: string;
  price: string;
  priceNote: string;
  body?: string;
  savings?: string;
  features: string[];
  hero?: boolean;
}
const MONTHLY_PLANS: Plan[] = [{
  name: 'Performance Prep',
  freq: '1x / week',
  ageNote: 'MS · Ages 11–13',
  price: '$150',
  priceNote: '/ month',
  features: ['4 sessions per month', '$37.50 per session', '60-min sessions', 'Tue or Thu 4:45–5:45 PM', 'Testing every 4 weeks']
}, {
  name: 'Performance Prep',
  freq: '2x / week',
  ageNote: 'MS · Ages 11–13',
  price: '$270',
  priceNote: '/ month',
  features: ['8 sessions per month', '$33.75 per session', '60-min sessions', 'Tue and Thu 4:45–5:45 PM', 'Testing every 4 weeks']
}, {
  name: 'Advanced Performance',
  freq: '1x / week',
  ageNote: 'HS · Ages 14+ · Court or Field',
  price: '$180',
  priceNote: '/ month',
  features: ['4 sessions per month', '$45 per session', '75-min sessions', 'Court 5:45 PM or Field 7:00 PM', 'Testing every 4 weeks']
}, {
  name: 'Advanced Performance',
  freq: '2x / week',
  ageNote: 'HS · Ages 14+ · Court or Field',
  price: '$325',
  priceNote: '/ month',
  features: ['8 sessions per month', '$40.63 per session', '75-min sessions', 'Court 5:45 PM or Field 7:00 PM', 'Testing every 4 weeks'],
  hero: true
}, {
  name: 'Homeschool Multisport',
  freq: '2x / week only',
  ageNote: 'Homeschool Athletes · New',
  price: '$325',
  priceNote: '/ month',
  savings: 'Early Bird',
  features: ['Early bird rate through Aug 9, then $400', '8 sessions per month', '90-minute sessions', 'Tue and Thu 2:30–4:00 PM', 'Founding group capped at 10']
}];
const PRIVATE_1ON1: Plan[] = [{
  name: '1x / week',
  freq: 'Base Rate',
  price: '$600',
  priceNote: '/ month',
  features: ['4 sessions per month', '$150 per session', 'No discount']
}, {
  name: '2x / week',
  freq: 'Save 10%',
  price: '$1,080',
  priceNote: '/ month',
  savings: 'Save $120/mo',
  features: ['8 sessions per month', '$135 per session'],
  hero: true
}, {
  name: '3x / week',
  freq: 'Save 15%',
  price: '$1,530',
  priceNote: '/ month',
  savings: 'Save $270/mo',
  features: ['12 sessions per month', '$127.50 per session']
}];
const PRIVATE_2ON1: Plan[] = [{
  name: '1x / week',
  freq: 'Base Rate',
  price: '$500',
  priceNote: '/ month per athlete',
  features: ['4 sessions per month', '$125 per session', 'No discount']
}, {
  name: '2x / week',
  freq: 'Save 10%',
  price: '$900',
  priceNote: '/ month per athlete',
  features: ['8 sessions per month', '$112.50 per session'],
  hero: true
}, {
  name: '3x / week',
  freq: 'Save 15%',
  price: '$1,275',
  priceNote: '/ month per athlete',
  features: ['12 sessions per month', '$106.25 per session']
}];
const SINGLE_PLANS: Plan[] = [{
  name: 'Drop-In',
  freq: 'Single session',
  price: '$50',
  priceNote: '/ session',
  body: 'One class, any age group.',
  features: ['Try any age tier', 'No commitment', 'Reserve same week']
}, {
  name: '10-Session Card',
  freq: '10 sessions',
  price: '$450',
  priceNote: 'total',
  body: '$45 per session, fully flexible.',
  features: ['Use through Dec 17', 'Any age tier', 'Share within family'],
  hero: true
}, {
  name: 'Private 1-on-1',
  freq: 'Starting at',
  price: '$150',
  priceNote: '/ session',
  body: 'Final rate varies based on training frequency. The more sessions per week, the lower the per-session rate.',
  features: ['60 min with Coach Fernando', 'Custom programming', 'Position & sport-specific', 'Volume discounts available']
}];
const INCLUDED: string[] = ['Access to on-site recovery zone', 'Periodized programming', 'Performance testing every 4 weeks', 'Speed gates & jump mat testing', '10% sibling discount on additional athletes'];
const PRIVATE_INCLUDED: string[] = ["Individualized program built around your athlete's sport and goals.", 'Direct coaching from Fernando Beltran, Director of Performance.', 'Performance testing every 4 weeks, same markers, real data.', 'Full access to INPOWERiQ on-site recovery zone.', 'Speed and strength integrated into every session.', 'Make-ups allowed within the same week.'];
const POLICIES = [{
  Icon: RefreshCcw,
  title: 'Make-Ups',
  body: '1 free make-up per month, any age tier.'
}, {
  Icon: Calendar,
  title: 'Pay-In-Full',
  body: 'Full-season enrollments are non-refundable.'
}, {
  Icon: Users,
  title: 'Holiday Closures',
  body: 'Closed Thanksgiving week, Nov 24 and 26. All other weeks run.'
}, {
  Icon: Eye,
  title: 'Parents Welcome',
  body: 'Observe any session from the parent gallery.'
}];
export function Pricing() {
  const [tab, setTab] = useState<Tab>('monthly');
  const plans = tab === 'monthly' ? MONTHLY_PLANS : tab === 'single' ? SINGLE_PLANS : [];
  return <div className="w-full" data-id="element-649" data-mp="35nf0q6zkjopp">
      {/* HERO */}
      <section className="relative bg-reach-black overflow-hidden" data-id="element-650" data-mp="39n5vsmw4or01">
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-651" data-mp="2vn4lyhxvy98f">
          <div className="absolute top-1/4 -left-40 w-[50%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-50" data-id="element-652" data-mp="2vp90dtteuqmy" />
          <div className="absolute top-[42%] -left-40 w-[35%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-30" data-id="element-653" data-mp="tvrrynxghlr5" />
          <div className="absolute top-[60%] -left-40 w-[25%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-20" data-id="element-654" data-mp="yve5kyh7540h" />
        </div>
        <div className="max-w-container mx-auto px-4 md:px-8 py-20 md:py-24 text-center relative z-10" data-id="element-655" data-mp="xfa4pwtr8a3r">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-4" data-id="element-656" data-mp="sd1akx55b202">
            Fall 2026 · Enrollment Open · Starts Aug 11
          </p>
          <h1 className="font-bebas text-white text-6xl md:text-8xl tracking-wide leading-none mb-4" data-id="element-657" data-mp="ihhrxcfldzt9">
            Pricing
          </h1>
          <p className="font-inter text-white/85 text-base md:text-xl max-w-2xl mx-auto" data-id="element-658" data-mp="1g69n2f6qulo2">
            Group training, private coaching, and drop-in options.
          </p>
        </div>
      </section>

      {/* TAB TOGGLE */}
      <section className="bg-white sticky top-[64px] md:top-[72px] z-30 border-b border-black/5" data-id="element-659" data-mp="11fahld17gd8p">
        <div className="max-w-container mx-auto px-4 md:px-8 py-5" data-id="element-660" data-mp="1yuodp1d5fxoc">
          <div className="flex justify-center" data-id="element-661" data-mp="1yvzhv2aib7fz">
            <div role="tablist" aria-label="Pricing options" className="inline-flex bg-reach-gray rounded-[4px] p-1 w-full sm:w-auto" data-id="element-662" data-mp="1u3g34203269l">
              <TabButton active={tab === 'monthly'} onClick={() => setTab('monthly')} data-id="element-663" data-mp="26dxql4kv6ei5">
                Monthly
              </TabButton>
              <TabButton active={tab === 'private'} onClick={() => setTab('private')} data-id="element-664" data-mp="2gcdgx5c2hjql">
                Private Training
              </TabButton>
              <TabButton active={tab === 'single'} onClick={() => setTab('single')} data-id="element-665" data-mp="2l9na8w2kufjp">
                Drop-In
              </TabButton>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING CARDS */}
      {tab === 'private' ? <PrivateTrainingTab data-id="element-666" data-mp="341xzvo880gzv" /> : <section className="bg-white py-16 md:py-20" data-id="element-667" data-mp="3430nnna8y3bk">
          <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-668" data-mp="39127iafhy6o3">
            {/* Helper line for monthly tab */}
            {tab === 'monthly' && <p className="font-inter text-center text-sm text-reach-black/60 uppercase tracking-[0.2em] mb-8" data-id="element-669" data-mp="3uao9gb3pini7">
                Fall runs Tue and Thu · Train 1x or 2x per week · Homeschool is
                2x only
              </p>}

            <div className={`grid gap-5 md:gap-6 ${tab === 'single' ? 'grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto'}`} data-id="element-670" data-mp="286whf5vuv13">
              {plans.map(p => <PlanCard key={`${p.name}-${p.freq}`} plan={p} data-id="element-671" data-mp="36npvbk327rng" />)}
            </div>

            {tab === 'monthly' && <p className="font-inter text-center text-sm text-reach-black/60 mt-8 max-w-2xl mx-auto" data-id="element-672" data-mp="3kfnzdghwyxcr">
                Homeschool early bird rate is locked for enrolled athletes
                through the fall season. A Monday and Wednesday block opens as
                Tuesday and Thursday groups fill.
              </p>}

            <p className="font-inter text-center text-sm text-reach-black/60 mt-6 max-w-2xl mx-auto" data-id="element-673" data-mp="12g4y0hxgvopr">
              All prices in USD. Auto-renewal on monthly plans can be cancelled
              with 14 days notice before the next billing cycle.
            </p>
          </div>
        </section>}

      {/* WHAT'S INCLUDED */}
      {tab !== 'private' && <section className="bg-reach-gray py-20 md:py-24" data-id="element-674" data-mp="ycffoa7dy3d6">
          <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-675" data-mp="1m5p25f2m08rg">
            <div className="text-center mb-10" data-id="element-676" data-mp="1q6hfokiikztj">
              <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-677" data-mp="1q56calszdu2y">
                Every Plan Includes
              </p>
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-678" data-mp="1h4hwblk11jnf">
                What's Included
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-[4px] p-6 md:p-8 border-l-4 border-reach-red" data-id="element-679" data-mp="tdx26oeywsb">
              {INCLUDED.map(item => <li key={item} className="flex items-start gap-3" data-id="element-680" data-mp="k89ts7pf6pep">
                  <div className="w-6 h-6 bg-reach-red rounded-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-681" data-mp="p74bf6bni7x8">
                    <Check size={14} className="text-white" strokeWidth={3} data-id="element-682" data-mp="ah5grdjb1emi" />
                  </div>
                  <span className="font-inter text-reach-black text-base" data-id="element-683" data-mp="ffqf17ezxo8b">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </section>}

      {/* POLICIES */}
      {tab !== 'private' && <section className="bg-white py-16" data-id="element-684" data-mp="3np9wsu4gs9vl">
          <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-685" data-mp="3nqbal6cv7ah0">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-6 text-center" data-id="element-686" data-mp="3somgx73yb2uh">
              Policies
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" data-id="element-687" data-mp="1qw6madgecyk3">
              {POLICIES.map(({
            Icon,
            title,
            body
          }) => <div key={title} className="flex items-start gap-3" data-id="element-688" data-mp="3ber1obdj5rzx">
                  <Icon size={18} className="text-reach-red shrink-0 mt-1" data-id="element-689" data-mp="36fxm8deonqx4" />
                  <div data-id="element-690" data-mp="3l3sfpw0ilzb9">
                    <p className="font-bebas text-lg tracking-wider uppercase mb-1" data-id="element-691" data-mp="3l53roj446ddg">
                      {title}
                    </p>
                    <p className="font-inter text-sm text-reach-black/70 leading-relaxed" data-id="element-692" data-mp="3gb8zerag0q2r">
                      {body}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>}

      {/* FINAL CTA */}
      <section className="bg-reach-red py-16 md:py-20" data-id="element-693" data-mp="7x0gkaxq3dns">
        <div className="max-w-container mx-auto px-4 md:px-8 text-center" data-id="element-694" data-mp="2xw8jnmjblz1">
          <h2 className="font-bebas text-white text-5xl md:text-7xl tracking-wide mb-4" data-id="element-695" data-mp="29ovnyecy4w07">
            Ready to Start?
          </h2>
          <p className="font-inter text-white text-lg md:text-xl mb-10 max-w-2xl mx-auto" data-id="element-696" data-mp="24qkei4qkcnii">
            Reserve your athlete's spot for Fall 2026.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-md sm:max-w-none mx-auto" data-id="element-697" data-mp="2ugajwek2mynt">
            <a href="#top" onClick={e => {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }} className="inline-flex items-center justify-center bg-white text-reach-black font-inter font-semibold px-6 min-h-[44px] md:min-h-[48px] rounded-[4px] transition-all duration-200 ease-out hover:bg-reach-black hover:text-white uppercase tracking-wide text-sm md:text-base hover:-translate-y-[3px]" data-id="element-698" data-mp="33ecspga1qvln">
              Choose a Plan
            </a>
            <Link to="/book" className="btn-secondary-dark sm:w-auto" data-id="element-699" data-mp="3w4cq6u27x6eh">
              Book Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>;
}
/* ---------- Private Training Tab ---------- */
function PrivateTrainingTab() {
  return <>
      <section className="bg-white py-16 md:py-20" data-id="element-700" data-mp="1kfonwnozcd3l">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-701" data-mp="1ki11konpg1au">
          {/* 1-on-1 Stack */}
          <div className="mb-16" data-id="element-702" data-mp="1fixznd30ub73">
            <div className="text-center mb-8" data-id="element-703" data-mp="1aia90sipfwde">
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide mb-2" data-id="element-704" data-mp="1ajbod96r750x">
                1-on-1 Private Coaching
              </h2>
              <p className="font-inter text-reach-black/70 text-base" data-id="element-705" data-mp="236plp8ed62lk">
                Base rate $150 per session
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto" data-id="element-706" data-mp="1ye4umemue3lq">
              {PRIVATE_1ON1.map(p => <PlanCard key={`${p.name}-${p.freq}`} plan={p} data-id="element-707" data-mp="2qw0ea2lk8qom" />)}
            </div>
          </div>

          {/* 2-on-1 Stack */}
          <div data-id="element-708" data-mp="2m48wagggzta3">
            <div className="text-center mb-8" data-id="element-709" data-mp="2m3quz5eyaovb">
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide mb-2" data-id="element-710" data-mp="26nk1sfov22yr">
                2-on-1 Semi-Private
              </h2>
              <p className="font-inter text-reach-black/70 text-base" data-id="element-711" data-mp="2bn53gxvf0pf6">
                Base rate $125 per athlete per session
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto" data-id="element-712" data-mp="21nxok6918mpy">
              {PRIVATE_2ON1.map(p => <PlanCard key={`${p.name}-${p.freq}`} plan={p} data-id="element-713" data-mp="2pf33mj75faz7" />)}
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="bg-reach-gray py-16 md:py-20" data-id="element-714" data-mp="2foib9di2j2oq">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-715" data-mp="2flw43ez3ap71">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-716" data-mp="2km0c0glxmbln">
            <div className="bg-white rounded-[4px] p-8 border-l-4 border-reach-red" data-id="element-717" data-mp="38ce241kyi0dl">
              <h3 className="font-bebas text-2xl md:text-3xl tracking-wide mb-4" data-id="element-718" data-mp="3d3w4mu2mo90o">
                Who 1-on-1 is for
              </h3>
              <p className="font-inter text-reach-black text-base leading-relaxed" data-id="element-719" data-mp="11n2maeuogc4r">
                Coming off injury. In-season position work. Recruiting-stage
                athletes who need every rep to count. Collegiate and pro prep.
                Anyone who wants the fastest possible progress with full coach
                attention.
              </p>
            </div>
            <div className="bg-white rounded-[4px] p-8 border-l-4 border-reach-red" data-id="element-720" data-mp="ioq4y5tm3vy3">
              <h3 className="font-bebas text-2xl md:text-3xl tracking-wide mb-4" data-id="element-721" data-mp="dqy8cyi0vlnk">
                Who 2-on-1 is for
              </h3>
              <p className="font-inter text-reach-black text-base leading-relaxed" data-id="element-722" data-mp="rrr8pzv09ayl">
                Siblings. Training partners. Teammates. Private-level coaching
                at a lower per-athlete rate. Both athletes commit to the same
                schedule and frequency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Every Private Membership Includes */}
      <section className="bg-white py-16 md:py-20" data-id="element-723" data-mp="vslwg7ihfp2g">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-724" data-mp="10rf9xzxrrg2m">
          <div className="text-center mb-10" data-id="element-725" data-mp="10u1h3ygqztkb">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-726" data-mp="15qrwskon4n57">
              Every Private Membership Includes
            </p>
            <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-727" data-mp="d2cmgbgget07">
              What's Included
            </h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-reach-gray rounded-[4px] p-6 md:p-8 border-l-4 border-reach-red" data-id="element-728" data-mp="i28q29l8oif8">
            {PRIVATE_INCLUDED.map(item => <li key={item} className="flex items-start gap-3" data-id="element-729" data-mp="qu0ykeqpdhh6">
                <div className="w-6 h-6 bg-reach-red rounded-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-730" data-mp="3qh8ud7xuec6r">
                  <Check size={14} className="text-white" strokeWidth={3} data-id="element-731" data-mp="3uica7ufw487z" />
                </div>
                <span className="font-inter text-reach-black text-base" data-id="element-732" data-mp="3mzuxzggmqfbb">
                  {item}
                </span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Fine Print */}
      <section className="bg-reach-gray py-12" data-id="element-733" data-mp="3dxlp0v10eu01">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-734" data-mp="3e0hlsg14fcmw">
          <p className="font-inter text-reach-black/70 text-sm leading-relaxed text-center" data-id="element-735" data-mp="38ys8suuc8ow6">
            Monthly memberships auto renew. Cancel anytime with 7 days notice
            before the next billing cycle. Missed sessions can be made up within
            the same week with 24 hour notice. 2-on-1 pricing is per athlete.
            Both athletes are billed separately. Sibling discount stacks on
            2-on-1 packages when both athletes are siblings. Private sessions
            run 60 or 75 minutes depending on age and goals.
          </p>
        </div>
      </section>
    </>;
}
/* ---------- Subcomponents ---------- */
function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button role="tab" aria-selected={active} onClick={onClick} className={`flex-1 sm:flex-none px-4 sm:px-6 py-2.5 min-h-[44px] rounded-[4px] font-inter text-xs sm:text-sm font-semibold uppercase tracking-wider transition-all duration-200 ${active ? 'bg-reach-black text-white' : 'text-reach-black/70 hover:text-reach-black'}`} data-id="element-736" data-mp="d1jdfvxb6b8k">
      {children}
    </button>;
}
function PlanCard({
  plan
}: {
  plan: Plan;
}) {
  const isDark = !!plan.hero;
  return <div className={`relative rounded-[4px] p-6 md:p-7 flex flex-col transition-all duration-200 ease-out overflow-hidden border-b-4 ${isDark ? 'bg-reach-black text-white ring-2 ring-reach-red border-transparent' : 'bg-reach-gray text-reach-black border-transparent hover:-translate-y-1 hover:border-reach-red'}`} data-id="element-737" data-mp="3cgobn3v7r06x">
      <div className="mt-2" data-id="element-738" data-mp="37ga5jrd6a0bj">
        <p className="font-inter text-xs uppercase tracking-[0.25em] font-semibold mb-2 text-reach-red" data-id="element-739" data-mp="37gjudbmhe1fn">
          {plan.freq}
        </p>
        <h3 className="font-bebas text-2xl md:text-3xl tracking-wide leading-none mb-2" data-id="element-740" data-mp="2iwowdghacuz0">
          {plan.name}
        </h3>
        {plan.ageNote && <p className={`font-inter text-xs uppercase tracking-wider mb-5 ${isDark ? 'text-white/60' : 'text-reach-black/60'}`} data-id="element-741" data-mp="2xv0tfo8km5qp">
            {plan.ageNote}
          </p>}
        {plan.body && <p className={`font-inter text-sm mb-5 leading-relaxed ${isDark ? 'text-white/85' : 'text-reach-black/75'}`} data-id="element-742" data-mp="31vaxuwgpso3g">
            {plan.body}
          </p>}
      </div>

      <div className="mb-6 flex items-baseline gap-2 flex-wrap" data-id="element-743" data-mp="3bl48nv918z6n">
        <span className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-744" data-mp="3blw5taia34z1">
          {plan.price}
        </span>
        <span className={`font-inter text-sm ${isDark ? 'text-white/70' : 'text-reach-black/60'}`} data-id="element-745" data-mp="3pij1l9yhvp3d">
          {plan.priceNote}
        </span>
        {plan.savings && <span className={`ml-auto inline-block font-inter text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-[4px] ${isDark ? 'bg-white text-reach-red' : 'bg-reach-red text-white'}`} data-id="element-746" data-mp="1yqnqqh2vaf5">
            {plan.savings}
          </span>}
      </div>

      <ul className="space-y-2.5 mb-7 flex-grow" data-id="element-747" data-mp="5z0s5yp81srw">
        {plan.features.map(f => <li key={f} className={`flex items-start gap-2.5 text-sm ${isDark ? 'text-white/85' : 'text-reach-black/85'}`} data-id="element-748" data-mp="3eekhimmrur1o">
            <Check size={16} className="text-reach-red shrink-0 mt-0.5" data-id="element-749" data-mp="3o4duns2kbe81" />
            <span data-id="element-750" data-mp="3o5ozzwbnou19">{f}</span>
          </li>)}
      </ul>

      <Link to="/book" className={isDark ? 'btn-primary w-full' : 'btn-secondary w-full'} data-id="element-751" data-mp="3jbkpj336m3vf">
        Enroll Now <ArrowRight size={16} className="ml-2" data-id="element-752" data-mp="2vlgp14laik8n" />
      </Link>
    </div>;
}